import React, { useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { getPostBySlug, relatedPosts, blogAuthor } from '../data/blogPosts';
import Seo from '../components/Seo';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import BookingModal from '../components/BookingModal';
import { Clock, Calendar, ArrowLeft, ArrowRight, Leaf } from 'lucide-react';
import { Button } from '../components/ui/button';

const BlogPost = () => {
  const { slug } = useParams();
  const post = getPostBySlug(slug);
  const [bookOpen, setBookOpen] = useState(false);

  if (!post) return <Navigate to="/blog" replace />;

  const related = relatedPosts(post.slug, post.tag);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.description,
    image: post.image,
    datePublished: post.date,
    dateModified: post.date,
    author: { '@type': 'Person', name: blogAuthor.name },
    publisher: {
      '@type': 'Organization',
      name: 'NutrifyMe',
      logo: { '@type': 'ImageObject', url: 'https://images.unsplash.com/photo-1543352632-5a4b24e4d2a6?w=400' },
    },
    mainEntityOfPage: `/blog/${post.slug}`,
    keywords: post.keywords.join(', '),
  };

  const faqJsonLd = post.faq && post.faq.length > 0 ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: post.faq.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  } : null;

  return (
    <div className="min-h-screen bg-[#faf7f1]">
      <Seo
        title={post.metaTitle.replace(' | NutrifyMe', '')}
        description={post.description}
        keywords={post.keywords}
        image={post.image}
        path={`/blog/${post.slug}`}
        type="article"
        publishedDate={post.date}
        jsonLd={articleJsonLd}
      />
      <Navbar onBook={() => setBookOpen(true)} />

      <article className="pt-32 pb-20">
        <div className="max-w-3xl mx-auto px-6">
          <Link to="/blog" className="text-sm text-[#7a8471] mb-6 inline-flex items-center gap-2 hover:text-[#3a5a2c]">
            <ArrowLeft className="w-4 h-4" /> All articles
          </Link>

          <div className="flex items-center gap-3 mb-5 text-xs">
            <span className="tracking-widest uppercase text-[#c9743a] bg-[#fce7d5] px-2.5 py-1 rounded-full">{post.tag}</span>
            <span className="inline-flex items-center gap-1 text-[#7a8471]"><Clock className="w-3 h-3" /> {post.readMins} min read</span>
            <span className="inline-flex items-center gap-1 text-[#7a8471]"><Calendar className="w-3 h-3" /> {post.date}</span>
          </div>

          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#1a2b17] leading-[1.08] tracking-tight">
            {post.title}
          </h1>
          <p className="mt-6 text-xl text-[#4a5544] leading-relaxed">{post.intro}</p>

          <div className="mt-8 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#e8f2e2] flex items-center justify-center"><Leaf className="w-4 h-4 text-[#3a5a2c]" /></div>
            <div>
              <div className="text-sm font-medium text-[#1a2b17]">{blogAuthor.name}</div>
              <div className="text-xs text-[#7a8471]">{blogAuthor.role}</div>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-6 mt-12">
          <div className="aspect-[16/9] rounded-3xl overflow-hidden">
            <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-6 mt-14">
          {post.sections.map((s, i) => (
            <section key={i} className="mb-10">
              <h2 className="font-serif text-3xl text-[#1a2b17] mb-4 tracking-tight">{s.heading}</h2>
              <p className="text-[#2a3a24] leading-[1.85] text-lg">{s.body}</p>
            </section>
          ))}

          {post.faq && post.faq.length > 0 && (
            <section className="mt-16 bg-white rounded-3xl p-8 md:p-10">
              <h2 className="font-serif text-2xl text-[#1a2b17] mb-6">Frequently asked</h2>
              <div className="space-y-5">
                {post.faq.map((f, i) => (
                  <div key={i}>
                    <div className="font-medium text-[#1a2b17]">{f.q}</div>
                    <div className="mt-1 text-[#4a5544] leading-relaxed">{f.a}</div>
                  </div>
                ))}
              </div>
            </section>
          )}

          <div className="mt-14 p-8 md:p-10 rounded-3xl bg-gradient-to-br from-[#3a5a2c] to-[#4d6f3d] text-white">
            <div className="text-xs tracking-[0.2em] uppercase text-[#f4d5a8] mb-2">Ready to start?</div>
            <h3 className="font-serif text-3xl">Get a plan built for you</h3>
            <p className="text-white/80 mt-3">Book a free 15 minute consultation with Dietitian Prachi and get a personalised path forward.</p>
            <div className="mt-5 flex gap-3 flex-wrap">
              <Button onClick={() => setBookOpen(true)} className="rounded-full bg-white text-[#1a2b17] hover:bg-[#f4d5a8]">Book free call <ArrowRight className="w-4 h-4 ml-2" /></Button>
              <Button asChild variant="outline" className="rounded-full bg-transparent border-white/40 text-white hover:bg-white/10"><Link to="/quiz">Take diet quiz</Link></Button>
            </div>
          </div>
        </div>

        {faqJsonLd && (
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />
        )}

        {related.length > 0 && (
          <section className="max-w-6xl mx-auto px-6 mt-24">
            <h3 className="font-serif text-3xl text-[#1a2b17] mb-8">You might also like</h3>
            <div className="grid md:grid-cols-3 gap-6">
              {related.map((r) => (
                <Link to={`/blog/${r.slug}`} key={r.slug} className="group bg-white rounded-3xl overflow-hidden hover:shadow-xl transition-all">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={r.image} alt={r.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  </div>
                  <div className="p-6">
                    <h4 className="font-serif text-lg text-[#1a2b17] group-hover:text-[#3a5a2c]">{r.title}</h4>
                    <div className="mt-3 inline-flex items-center gap-1 text-sm text-[#c9743a]">Read <ArrowRight className="w-3 h-3" /></div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </article>

      <Footer onBook={() => setBookOpen(true)} />
      <BookingModal open={bookOpen} onClose={() => setBookOpen(false)} />
    </div>
  );
};

export default BlogPost;
